/* UI-DESIGN: LB-72 phone concept sheet. Three phone-first directions for the
   whole Home flow at 393px, all in the desktop's world: same lightbox room,
   type, copy, glass and case-file frames. They differ only in layout and
   interaction (Taco 2026-10-18: optimise the desktop for phones, not a new
   look). /preview?phone shows the sheet (three live frames side by side);
   /preview?phone=a|b|c is one direction full screen, for a real iPhone.
   Review-only: nothing here replaces the live Home until Taco picks. */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { ArrowLeft, ArrowRight, Bars, Persons, Xmark } from "@gravity-ui/icons";
import LightboxSurface from "../../../components/lightbox/LightboxSurface";
import GlassPanel from "../../../components/lightbox/GlassPanel";
import LightboxButton from "../../../components/lightbox/LightboxButton";
import { FALLBACK_CASES } from "../../../components/lightbox/caseView";
import type { CaseView } from "../../../components/lightbox/caseView";
import { frameFor, heroFrame } from "../../../components/lightbox/frames";
import { initialTypeface } from "../../../components/lightbox/typefaces";
import logoB from "../../../assets/brand/logo-b-red-pencil.svg";
import styles from "./PhoneConcepts.module.scss";

type Direction = "a" | "b" | "c";

const DIRECTIONS: { id: Direction; name: string; idea: string }[] = [
  {
    id: "a",
    name: "A. The desktop, restacked",
    idea: "Same order as desktop. Mysteries swipe sideways, Host stays in reach at the bottom, the menu is an index card.",
  },
  {
    id: "b",
    name: "B. One case at a time",
    idea: "Desktop's order: hero, What do you get, How it works, then the cases dealt one at a time, then Host. The menu drops out of a full-width glass bar.",
  },
  {
    id: "c",
    name: "C. Chapters",
    idea: "Desktop's scroll stops, one screen each. A rail on the edge shows where you are and the button changes per chapter.",
  },
];

/* Home copy, word for word (src/pages/home/index.tsx) */
const HERO_TITLE = "A Murder Mystery kit waiting for you.";
const HERO_SUB = "Host a murder mystery with your friends that will continue to change as the night progresses.";
const GETS_LINE = "A murder mystery that you play on your phone with your friends, with everything you need in one place.";
const GETS = [
  { title: "Suspects", text: "Everyone gets a character with their own backstory, plus a few secrets about the people next to them." },
  { title: "Clues", text: "The night plays out in rounds, and every round drops new clues on everyone's phone." },
  { title: "Questioning", text: "Then you grill each other until somebody slips up." },
];
const STEPS = [
  { title: "Pick a case", text: "Find the mystery that fits your crew and how many people are coming." },
  { title: "Invite your friends", text: "Name the party and send the link. Everyone RSVPs from their phone." },
  { title: "Live texts all night", text: "Updates about your character land on your phone as the night goes on." },
  { title: "You play too", text: "The host gets a character too, so nobody has to sit out and run the game." },
];
const NAV = [
  { to: "/mysteries", label: "Mysteries" },
  { to: "/open-cases", label: "Open Cases" },
  { to: "/about", label: "How it works" },
  { to: "/create-game", label: "Host a party" },
];

const CASES = FALLBACK_CASES;
const noop = () => undefined;

const useHost = () => {
  const navigate = useNavigate();
  return () => navigate("/create-game");
};

/* ---------- shared pieces ---------- */

const Bar = ({ onMenu, menuOpen, line = false }: { onMenu: () => void; menuOpen: boolean; line?: boolean }) => (
  <GlassPanel as="header" variant="panel" className={clsx(styles.bar, line && styles.barLine)}>
    <Link to="/" aria-label="Whodunit home">
      <img className={styles.logo} src={logoB} alt="" draggable={false} />
    </Link>
    <button type="button" className={styles.menuButton} onClick={onMenu} aria-expanded={menuOpen} aria-label={menuOpen ? "Close menu" : "Open menu"}>
      {menuOpen ? <Xmark width={22} height={22} /> : <Bars width={22} height={22} />}
    </button>
  </GlassPanel>
);

/* The menu as an index card dropped on the table (A and C) */
const IndexCard = ({ open, onClose, extra }: { open: boolean; onClose: () => void; extra?: ReactNode }) => (
  <div className={clsx(styles.scrim, open && styles.scrimOpen)} onClick={onClose} aria-hidden={!open}>
    <nav className={clsx(styles.indexCard, open && styles.indexCardOpen)} aria-label="Main" onClick={(e) => e.stopPropagation()}>
      <span className={styles.tape} aria-hidden />
      {extra}
      <ul className={styles.cardLinks}>
        {NAV.map((n) => (
          <li key={n.to}>
            <Link to={n.to} className={styles.cardLink}>{n.label}</Link>
          </li>
        ))}
      </ul>
      <Link to="/login" className={styles.cardLogin}>Log in</Link>
    </nav>
  </div>
);

const CaseFacts = ({ view }: { view: CaseView }) => (
  <p className={styles.facts}>{[view.players, view.length].filter(Boolean).join(", ")}</p>
);

const Steps = ({ compact = false }: { compact?: boolean }) => (
  <ol className={clsx(styles.steps, compact && styles.stepsCompact)}>
    {STEPS.map((s, i) => (
      <li key={s.title} className={styles.step}>
        <span className={styles.stepNo} aria-hidden>{String(i + 1).padStart(2, "0")}</span>
        <div>
          <h3 className={styles.stepTitle}>{s.title}</h3>
          <p className={styles.text}>{s.text}</p>
        </div>
      </li>
    ))}
  </ol>
);

const Gets = () => (
  <ul className={styles.gets}>
    {GETS.map((g) => (
      <li key={g.title}>
        <h3 className={styles.getTitle}>{g.title}</h3>
        <p className={styles.text}>{g.text}</p>
      </li>
    ))}
  </ul>
);

const HostCard = ({ id }: { id: string }) => {
  const host = useHost();
  const navigate = useNavigate();
  return (
    <GlassPanel as="section" className={styles.card} aria-labelledby={id}>
      <h2 id={id} className={styles.title}>Ready to host?</h2>
      <p className={styles.text}>We'll handle the mystery. You bring the friends (and the snacks).</p>
      <div className={styles.actions}>
        <LightboxButton icon={<Persons />} trailing={<ArrowRight />} onPress={host}>Host a party</LightboxButton>
        <LightboxButton tone="secondary" onPress={() => navigate("/mysteries")}>Browse mysteries</LightboxButton>
      </div>
    </GlassPanel>
  );
};

/* ---------- A: the desktop, restacked ---------- */

const DirectionA = () => {
  const [menu, setMenu] = useState(false);
  const [active, setActive] = useState(0);
  const [showDock, setShowDock] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const host = useHost();

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const io = new IntersectionObserver(([e]) => setShowDock(!e.isIntersecting), { threshold: 0.15 });
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  const onRowScroll = () => {
    const row = rowRef.current;
    if (!row) return;
    setActive(Math.round(row.scrollLeft / (row.firstElementChild as HTMLElement).offsetWidth));
  };
  const view = CASES[Math.min(active, CASES.length - 1)];

  return (
    <div className={styles.page}>
      <Bar onMenu={() => setMenu((m) => !m)} menuOpen={menu} />
      <IndexCard open={menu} onClose={() => setMenu(false)} />

      <section ref={heroRef} className={styles.heroA} aria-labelledby="a-hero">
        <img className={styles.heroFrame} src={heroFrame} alt="" />
        <GlassPanel className={styles.card}>
          <h1 id="a-hero" className={styles.hero}>{HERO_TITLE}</h1>
          <p className={styles.text}>{HERO_SUB}</p>
        </GlassPanel>
      </section>

      <section className={styles.section} aria-labelledby="a-cases">
        <h2 id="a-cases" className={clsx(styles.title, styles.pad)}>Pick a mystery</h2>
        <div ref={rowRef} className={styles.swipeRow} onScroll={onRowScroll}>
          {CASES.map((c) => (
            <figure key={c.id} className={styles.swipeItem}>
              <img src={frameFor(c.name) ?? ""} alt={`${c.name} case file`} className={styles.swipeFrame} />
            </figure>
          ))}
        </div>
        <div className={styles.dots} aria-hidden>
          {CASES.map((c, i) => <span key={c.id} className={clsx(styles.dot, i === active && styles.dotOn)} />)}
        </div>
        <GlassPanel className={clsx(styles.card, styles.caseInfo)}>
          <h3 className={styles.caseName}>{view.name}</h3>
          <CaseFacts view={view} />
          <p className={styles.text}>{view.premise}</p>
          <LightboxButton trailing={<ArrowRight />} onPress={host}>Try this case</LightboxButton>
        </GlassPanel>
      </section>

      <GlassPanel as="section" className={clsx(styles.card, styles.section)} aria-labelledby="a-gets">
        <h2 id="a-gets" className={styles.title}>What do you get?</h2>
        <p className={styles.text}>{GETS_LINE}</p>
        <Gets />
      </GlassPanel>

      <GlassPanel as="section" className={clsx(styles.card, styles.section)} aria-labelledby="a-how">
        <h2 id="a-how" className={styles.title}>How it works</h2>
        <Steps compact />
      </GlassPanel>

      <div className={styles.section}><HostCard id="a-host" /></div>

      <div className={clsx(styles.dock, showDock && styles.dockOn)}>
        <LightboxButton icon={<Persons />} trailing={<ArrowRight />} onPress={host}>Host a party</LightboxButton>
      </div>
    </div>
  );
};

/* B (round 2, Taco 2026-10-18): the folder drawer read clunky. The menu
   now drops out of the full-width bar as the same frosted glass, plain
   typeset links on hairlines, Log in last. */
const GlassDrop = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
  <div className={clsx(styles.scrim, styles.scrimUnder, open && styles.scrimOpen)} onClick={onClose} aria-hidden={!open}>
    <nav className={clsx(styles.drop, open && styles.dropOpen)} aria-label="Main" onClick={(e) => e.stopPropagation()}>
      <ul className={styles.dropLinks}>
        {NAV.map((n) => (
          <li key={n.to}>
            <Link to={n.to} className={styles.dropLink}>{n.label}</Link>
          </li>
        ))}
      </ul>
      <Link to="/login" className={styles.dropLogin}>Log in</Link>
    </nav>
  </div>
);

/* ---------- B: one case at a time ---------- */

const DirectionB = () => {
  const [menu, setMenu] = useState(false);
  const [i, setI] = useState(0);
  const [dx, setDx] = useState(0);
  const start = useRef<number | null>(null);
  const host = useHost();
  const go = (d: number) => setI((n) => (n + d + CASES.length) % CASES.length);
  const view = CASES[i];
  const behind = [1, 2].map((k) => CASES[(i + k) % CASES.length]);

  return (
    <div className={styles.page}>
      <Bar onMenu={() => setMenu((m) => !m)} menuOpen={menu} line />
      <GlassDrop open={menu} onClose={() => setMenu(false)} />

      {/* UI-DESIGN: the case file sits straight under the hero glass (no
          empty table between them) and its edges fade into the room */}
      <section className={styles.heroB} aria-labelledby="b-hero">
        <GlassPanel className={clsx(styles.card, styles.heroBCard)}>
          <h1 id="b-hero" className={styles.hero}>{HERO_TITLE}</h1>
          <p className={styles.text}>{HERO_SUB}</p>
        </GlassPanel>
        <img className={styles.heroBFile} src={heroFrame} alt="" />
      </section>

      {/* UI-DESIGN: LB-72 Taco's pick (B) in the desktop's cadence: hero,
          What do you get, How it works, then the cases, then Host */}
      <GlassPanel as="section" className={clsx(styles.card, styles.section)} aria-labelledby="b-gets">
        <h2 id="b-gets" className={styles.title}>What do you get?</h2>
        <p className={styles.text}>{GETS_LINE}</p>
        <Gets />
      </GlassPanel>

      <GlassPanel as="section" className={clsx(styles.card, styles.section)} aria-labelledby="b-how">
        <h2 id="b-how" className={styles.title}>How it works</h2>
        <Steps compact />
      </GlassPanel>

      <section className={styles.section} aria-labelledby="b-cases">
        <h2 id="b-cases" className={styles.srOnly}>Mysteries</h2>
        <div
          className={styles.stack}
          onPointerDown={(e) => { start.current = e.clientX; }}
          onPointerMove={(e) => { if (start.current !== null) setDx(e.clientX - start.current); }}
          onPointerUp={() => { if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1); start.current = null; setDx(0); }}
          onPointerCancel={() => { start.current = null; setDx(0); }}
        >
          {behind.reverse().map((c, k) => (
            <img key={c.id} src={frameFor(c.name) ?? ""} alt="" className={clsx(styles.stackFrame, styles[`behind${k}`])} draggable={false} />
          ))}
          <img
            key={view.id}
            src={frameFor(view.name) ?? ""}
            alt={`${view.name} case file`}
            className={clsx(styles.stackFrame, styles.stackTop)}
            style={{ transform: `translateX(${dx}px) rotate(${dx / 30}deg)` }}
            draggable={false}
          />
        </div>
        <div className={styles.stackNav}>
          <button type="button" className={styles.round} onClick={() => go(-1)} aria-label="Previous mystery"><ArrowLeft width={18} height={18} /></button>
          <p className={styles.stackCount}>{i + 1} of {CASES.length}</p>
          <button type="button" className={styles.round} onClick={() => go(1)} aria-label="Next mystery"><ArrowRight width={18} height={18} /></button>
        </div>
        <GlassPanel className={clsx(styles.card, styles.caseInfo)}>
          <h2 className={styles.caseName}>{view.name}</h2>
          <CaseFacts view={view} />
          <p className={styles.text}>{view.premise}</p>
          <LightboxButton trailing={<ArrowRight />} onPress={host}>Try this case</LightboxButton>
        </GlassPanel>
      </section>

      <div className={styles.section}><HostCard id="b-host" /></div>
    </div>
  );
};

/* ---------- C: chapters ---------- */

const CHAPTERS = ["Start", "What you get", "How it works", ...CASES.map((c) => c.name), "Host"];

const DirectionC = () => {
  const [menu, setMenu] = useState(false);
  const [chapter, setChapter] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);
  const host = useHost();

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setChapter(Number((e.target as HTMLElement).dataset.i)); }),
      { threshold: 0.55 },
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const jump = (n: number) => { setMenu(false); refs.current[n]?.scrollIntoView({ behavior: "smooth" }); };
  const caseIndex = chapter - 3;
  const onCase = caseIndex >= 0 && caseIndex < CASES.length;
  const action = chapter === 0
    ? { label: "Pick a mystery", run: () => jump(3) }
    : onCase
      ? { label: "Try this case", run: host }
      : chapter === CHAPTERS.length - 1
        ? { label: "Host a party", run: host }
        : { label: "Next", run: () => jump(chapter + 1) };
  const ref = (n: number) => (el: HTMLElement | null) => { refs.current[n] = el; };

  return (
    <div className={clsx(styles.page, styles.chapters)}>
      <Bar onMenu={() => setMenu((m) => !m)} menuOpen={menu} />
      <IndexCard
        open={menu}
        onClose={() => setMenu(false)}
        extra={
          <ol className={styles.cardChapters}>
            {CHAPTERS.map((c, n) => (
              <li key={c}>
                <button type="button" className={clsx(styles.cardChapter, n === chapter && styles.cardChapterOn)} onClick={() => jump(n)}>{c}</button>
              </li>
            ))}
          </ol>
        }
      />

      <section ref={ref(0)} data-i={0} className={clsx(styles.chapter, styles.chapterHero)} aria-labelledby="c-hero">
        <img className={styles.heroFrame} src={heroFrame} alt="" />
        <GlassPanel className={styles.card}>
          <h1 id="c-hero" className={styles.hero}>{HERO_TITLE}</h1>
          <p className={styles.text}>{HERO_SUB}</p>
        </GlassPanel>
      </section>
      <section ref={ref(1)} data-i={1} className={styles.chapter} aria-labelledby="c-gets">
        <GlassPanel className={styles.card}>
          <h2 id="c-gets" className={styles.title}>What do you get?</h2>
          <p className={styles.text}>{GETS_LINE}</p>
          <Gets />
        </GlassPanel>
      </section>
      <section ref={ref(2)} data-i={2} className={styles.chapter} aria-labelledby="c-how">
        <GlassPanel className={styles.card}>
          <h2 id="c-how" className={styles.title}>How it works</h2>
          <Steps compact />
        </GlassPanel>
      </section>
      {CASES.map((c, k) => (
        <section key={c.id} ref={ref(3 + k)} data-i={3 + k} className={clsx(styles.chapter, styles.chapterCase)} aria-labelledby={`c-case-${k}`}>
          <img src={frameFor(c.name) ?? ""} alt={`${c.name} case file`} className={styles.chapterFrame} />
          <GlassPanel className={clsx(styles.card, styles.caseInfo)}>
            <h2 id={`c-case-${k}`} className={styles.caseName}>{c.name}</h2>
            <CaseFacts view={c} />
            <p className={styles.text}>{c.premise}</p>
          </GlassPanel>
        </section>
      ))}
      <section ref={ref(CHAPTERS.length - 1)} data-i={CHAPTERS.length - 1} className={styles.chapter}>
        <HostCard id="c-host" />
      </section>

      <ol className={styles.rail} aria-label="Chapters">
        {CHAPTERS.map((c, n) => (
          <li key={c}>
            <button type="button" className={clsx(styles.railTick, n === chapter && styles.railTickOn)} onClick={() => jump(n)} aria-label={c} aria-current={n === chapter ? "step" : undefined} />
          </li>
        ))}
      </ol>
      <div className={clsx(styles.dock, styles.dockOn)}>
        <LightboxButton trailing={<ArrowRight />} onPress={action.run}>{action.label}</LightboxButton>
      </div>
    </div>
  );
};

/* ---------- sheet ---------- */

const Solo = ({ id }: { id: Direction }) => (
  <LightboxSurface mode="frames" typeface={initialTypeface()} onUnavailable={noop} className={styles.solo}>
    <div data-phone-solo="">
      {id === "a" ? <DirectionA /> : id === "b" ? <DirectionB /> : <DirectionC />}
    </div>
  </LightboxSurface>
);

const Sheet = () => (
  <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={noop}>
    <main className={styles.sheet}>
      <GlassPanel className={clsx(styles.card, styles.sheetIntro)}>
        <h1 className={styles.title}>Phone directions</h1>
        <p className={styles.text}>
          Three ways to lay out the Home on a phone, all in the desktop's look. Scroll and tap inside each one. On your
          iPhone, open one full screen.
        </p>
      </GlassPanel>
      <div className={styles.frames}>
        {DIRECTIONS.map((d) => (
          <figure key={d.id} className={styles.frameCol}>
            <div className={styles.device}>
              <iframe title={d.name} src={`/preview?phone=${d.id}`} className={styles.iframe} />
            </div>
            <figcaption className={styles.caption}>
              <h2 className={styles.caseName}>{d.name}</h2>
              <p className={styles.text}>{d.idea}</p>
              <Link to={`/preview?phone=${d.id}`} className={styles.open}>Open full screen <ArrowRight width={16} height={16} aria-hidden /></Link>
            </figcaption>
          </figure>
        ))}
      </div>
    </main>
  </LightboxSurface>
);

const PhoneConcepts = ({ direction }: { direction: string }) =>
  direction === "a" || direction === "b" || direction === "c" ? <Solo id={direction} /> : <Sheet />;

export default PhoneConcepts;
